import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import './FeedPage.css';
import { useNavigate } from 'react-router-dom';
import {
  Favorite as FavoriteIcon,
  ChatBubble as ChatBubbleIcon,
  Share as ShareIcon,
  MoreVert as MoreVertIcon,
  Report as ReportIcon,
  Link as LinkIcon,
  ContentCopy as ContentCopyIcon,
  WhatsApp as WhatsAppIcon,
} from '@mui/icons-material';

import type { AddPostDraftDto, PostDto, PostCommentDto, PostLocationOptionDto } from './PostDto';
import { toAbsoluteUrl } from './PostDto';
import AddPost from './AddPost';
import IUser from '../../../main/interfaces/IUser';

const IMAGE_NOT_FOUND_PLACEHOLDER =
  'data:image/svg+xml;utf8,' +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="400" height="300" viewBox="0 0 400 300">
      <rect width="400" height="300" fill="#f3f4f6"/>
      <text x="200" y="150" text-anchor="middle" dominant-baseline="middle" font-family="Arial, sans-serif" font-size="22" fill="#6b7280">
        Image not found
      </text>
    </svg>`,
  );

type FeedPageProps = {
  posts: PostDto[];
  user?: IUser | null;
  topContent?: React.ReactNode;
  suggestedTags?: string[];
  locationOptions?: PostLocationOptionDto[];
  onLocationSelect?: (location: PostLocationOptionDto) => void;

  onAddPost: (content: string, images?: File[], extras?: Partial<AddPostDraftDto>) => Promise<void> | void;
  onAddComment: (postId: number, commentText: string) => Promise<void> | void;

  onToggleLike: (postId: number) => Promise<void> | void;
  isLiked: (postId: number) => boolean;

  shareOpenFor: number | null;
  onToggleShareMenu: (postId: number) => void;

  onShareNative: (postId: number) => Promise<void> | void;
  onShareWhatsapp: (postId: number) => void;
  onCopyShareLink: (postId: number) => Promise<void> | void;

  onOpenReport: (postId: number) => void;
  onDeletePost: (postId: number) => Promise<void> | void;
  onCelebrate?: (kind: 'post' | 'like' | 'comment' | 'commentsOpen', postId: number) => void;
};

const FeedPage: React.FC<FeedPageProps> = ({
  posts,
  user,
  topContent,
  suggestedTags = [],
  locationOptions = [],
  onLocationSelect,
  onAddPost,
  onAddComment,
  onToggleLike,
  isLiked,
  shareOpenFor,
  onToggleShareMenu,
  onShareNative,
  onShareWhatsapp,
  onCopyShareLink,
  onOpenReport,
  onDeletePost,
  onCelebrate,
}) => {
  const { i18n } = useTranslation();
  const currentLocale = i18n.resolvedLanguage || i18n.language || 'al';
  const navigate = useNavigate();
  const isAuthed = !!user;

  const [commentInputs, setCommentInputs] = useState<Record<number, string>>({});
  const [commentsOpen, setCommentsOpen] = useState<Record<number, boolean>>({});
  const [showAllComments, setShowAllComments] = useState<Record<number, boolean>>({});
  const [optionsOpenFor, setOptionsOpenFor] = useState<number | null>(null);
  const [selectedImageByPost, setSelectedImageByPost] = useState<Record<number, number>>({});

  const requireAuth = (e?: React.SyntheticEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    navigate('/login');
  };

  const toggleComments = (postId: number) => {
    setCommentsOpen((prev) => {
      const next = !prev[postId];
      return { ...prev, [postId]: next };
    });
  };

  const toggleShowAll = (postId: number) => {
    setShowAllComments((prev) => ({ ...prev, [postId]: !prev[postId] }));
  };

  const handleCommentChange = (postId: number, value: string) => {
    setCommentInputs((prev) => ({ ...prev, [postId]: value }));
  };

  const handleCommentSubmit = (postId: number) => {
    if (!isAuthed) return;
    const txt = (commentInputs[postId] ?? '').trim();
    if (!txt) return;
    onAddComment(postId, txt);
    setCommentInputs((prev) => ({ ...prev, [postId]: '' }));
  };

  const handleKeyPress = (e: React.KeyboardEvent, postId: number) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleCommentSubmit(postId);
    }
  };
  const toggleOptionsMenu = (postId: number) => {
    setOptionsOpenFor((prev) => (prev === postId ? null : postId));
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return i18n.t('mainLanding.feed.now', { defaultValue: 'Now' });
    if (diffMins < 60) return i18n.t('mainLanding.feed.minutesAgo', { defaultValue: '{{count}} min ago', count: diffMins });
    if (diffHours < 24) return i18n.t('mainLanding.feed.hoursAgo', { defaultValue: '{{count}} hours ago', count: diffHours });
    if (diffDays < 7) return i18n.t('mainLanding.feed.daysAgo', { defaultValue: '{{count}} days ago', count: diffDays });

    return date.toLocaleDateString(currentLocale, { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const getInitials = (name?: string) => (name?.charAt(0)?.toUpperCase() ? name.charAt(0).toUpperCase() : 'U');
  const getSelectedImageIndex = (postId: number, total: number) => {
    const idx = selectedImageByPost[postId] ?? 0;
    if (idx < 0 || idx >= total) return 0;
    return idx;
  };

  return (
    <div className="feed-page-container">
      <div className="feed-main">
        {topContent}

        {/* Add post: hide if not logged in (remove possibility) */}
        {isAuthed ? (
          <div className="post-card">
            <AddPost
              onAddPost={onAddPost}
              suggestedTags={suggestedTags}
              locationOptions={locationOptions}
              onLocationSelect={onLocationSelect}
            />
          </div>
        ) : null}

        {posts.length === 0 ? (
          <div className="no-posts-message">
            <div className="no-posts-icon">📝</div>
            <h3>{i18n.t('mainLanding.feed.emptyTitle', { defaultValue: 'No posts yet' })}</h3>
            <p>{i18n.t('mainLanding.feed.emptySubtitle', { defaultValue: 'Be the first one to post something!' })}</p>
          </div>
        ) : (
          posts.map((post, index) => {
            const open = !!commentsOpen[post.id];
            const all = !!showAllComments[post.id];
            const comments = post.comments ?? [];
            const visibleComments = open ? (all ? comments : comments.slice(0, 3)) : [];

            return (
              <div key={post.id} className="post-card" data-post-id={post.id} style={{ animationDelay: `${index * 0.1}s` }}>
                <div className="post-header">
                  <div className="post-user-info">
                    <div className="post-avatar">{getInitials(post.userName)}</div>
                    <div>
                      <h4 className="post-username">{post.userName}</h4>
                      <span className="post-date">{formatDate(post.createdAt)}</span>
                      {post.locationName ? <div className="post-location">{post.locationName}</div> : null}
                    </div>
                  </div>
                  <div className="post-options-wrap">
                    <button
                      className="post-options-btn"
                      type="button"
                      aria-label={i18n.t('mainLanding.feed.optionsAria', { defaultValue: 'Options' })}
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleOptionsMenu(post.id);
                      }}
                    >
                      <MoreVertIcon />
                    </button>
                    {optionsOpenFor === post.id && (
                      <div className="post-options-menu">
                        {/* DELETE – vetëm për autorin */}
                        {user?.id === post.userId && (
                          <button
                            className="post-options-item delete"
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (window.confirm(i18n.t('mainLanding.feed.confirmDelete', { defaultValue: 'Are you sure you want to delete this post?' }))) {
                                onDeletePost(post.id);
                                setOptionsOpenFor(null);
                              }
                            }}
                          >
                            {i18n.t('mainLanding.feed.deletePost', { defaultValue: 'Delete post' })}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <div className="post-content-wrapper">
                  <p className="post-content">{post.content}</p>

                  {post.images && post.images.length > 0 && (
                    <div className="post-images post-images-split">
                      <div className="post-image-main">
                        <img
                          src={toAbsoluteUrl(post.images[getSelectedImageIndex(post.id, post.images.length)])}
                          alt={`Post ${post.id} main`}
                          loading="lazy"
                          decoding="async"
                          onError={(e) => {
                            if (e.currentTarget.src !== IMAGE_NOT_FOUND_PLACEHOLDER) {
                              e.currentTarget.src = IMAGE_NOT_FOUND_PLACEHOLDER;
                            }
                          }}
                        />
                      </div>
                      {post.images.length > 1 && (
                        <div className="post-image-thumbs">
                          {post.images.map((img, idx) => (
                            <button
                              key={idx}
                              type="button"
                              className={`post-image-thumb ${getSelectedImageIndex(post.id, post.images.length) === idx ? 'active' : ''}`}
                              onClick={() => setSelectedImageByPost((prev) => ({ ...prev, [post.id]: idx }))}
                            >
                              <img src={toAbsoluteUrl(img)} alt={`Post ${post.id} thumb ${idx + 1}`} loading="lazy" decoding="async" />
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* ===== ICON BUTTONS ===== */}
                <div className="post-stats">
                  {/* LIKE: show count; disable toggle if user null */}
                  <button
                    type="button"
                    className={`postStatBtn postStatBtnLike ${isLiked(post.id) ? 'isLiked' : ''} ${!isAuthed ? 'isDisabled' : ''}`}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (!isAuthed) return;
                      if (!isLiked(post.id)) onCelebrate?.('like', post.id);
                      onToggleLike(post.id);
                    }}
                    aria-label={i18n.t('mainLanding.feed.likeAria', { defaultValue: 'Like' })}
                    title={!isAuthed ? i18n.t('mainLanding.feed.loginToLike', { defaultValue: 'Log in to like' }) : i18n.t('mainLanding.feed.like', { defaultValue: 'Like' })}
                    disabled={!isAuthed}
                  >
                    <FavoriteIcon fontSize="small" className="postStatIcon" /> {post.likeCount || 0}
                  </button>

                  {/* COMMENTS: everyone can toggle and read */}
                  <button
                    type="button"
                    className={`postStatBtn postStatBtnComment ${open ? 'isActive' : ''}`}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      toggleComments(post.id);
                    }}
                    aria-label={i18n.t('mainLanding.feed.commentsAria', { defaultValue: 'Comments' })}
                    title={open ? i18n.t('mainLanding.feed.hideComments', { defaultValue: 'Hide comments' }) : i18n.t('mainLanding.feed.showComments', { defaultValue: 'Show comments' })}
                  >
                    <ChatBubbleIcon fontSize="small" className="postStatIcon" /> {comments.length}
                  </button>

                  {/* SHARE: keep enabled for everyone (change if you want) */}
                  <div className="postStatWrap">
                    <button
                      type="button"
                      className="postStatBtn postStatBtnShare"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onToggleShareMenu(post.id);
                      }}
                      aria-label={i18n.t('mainLanding.feed.shareAria', { defaultValue: 'Share' })}
                      title={i18n.t('mainLanding.feed.share', { defaultValue: 'Share' })}
                    >
                      <ShareIcon fontSize="small" className="postStatIcon" />
                    </button>

                    {shareOpenFor === post.id && (
                      <div className="shareMenu" role="menu" aria-label={i18n.t('mainLanding.feed.shareMenu', { defaultValue: 'Share menu' })}>
                        <button type="button" className="shareMenuBtn" onClick={() => onShareNative(post.id)}>
                          <LinkIcon fontSize="small" /> {i18n.t('mainLanding.feed.shareOpen', { defaultValue: 'Share / Open' })}
                        </button>
                        <button type="button" className="shareMenuBtn" onClick={() => onShareWhatsapp(post.id)}>
                          <WhatsAppIcon fontSize="small" /> WhatsApp
                        </button>
                        <button type="button" className="shareMenuBtn" onClick={() => onCopyShareLink(post.id)}>
                          <ContentCopyIcon fontSize="small" /> {i18n.t('mainLanding.feed.copyLink', { defaultValue: 'Copy link' })}
                        </button>
                      </div>
                    )}
                  </div>

                  {/* REPORT: disable if user null */}
                  <button
                    type="button"
                    className={`postStatBtn postStatBtnReport isDanger ${!isAuthed ? 'isDisabled' : ''}`}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (!isAuthed) return;
                      onOpenReport(post.id);
                    }}
                    aria-label={i18n.t('mainLanding.feed.report', { defaultValue: 'Report' })}
                    title={!isAuthed ? i18n.t('mainLanding.feed.loginToReport', { defaultValue: 'Log in to report' }) : i18n.t('mainLanding.feed.report', { defaultValue: 'Report' })}
                    disabled={!isAuthed}
                  >
                    <ReportIcon fontSize="small" className="postStatIcon" />
                  </button>
                </div>

                {/* ===== COMMENTS (ONLY when open) ===== */}
                {open && (
                  <div className="post-comments-section">
                    <div className="post-comments">
                      {visibleComments.map((c: PostCommentDto) => (
                        <div key={c.id} className="comment">
                          <div className="comment-user-avatar">{getInitials(c.userName)}</div>
                          <div className="comment-content">
                            <div className="comment-header">
                              <span className="comment-username">{c.userName ?? `User ${c.userId}`}</span>
                              <span className="comment-time">{formatDate(c.createdAt)}</span>
                            </div>
                            <p className="comment-text">{c.comment}</p>
                          </div>
                        </div>
                      ))}

                      {comments.length > 3 && (
                        <button className="show-more-comments" onClick={() => toggleShowAll(post.id)} type="button">
                          {all
                            ? i18n.t('mainLanding.feed.showLess', { defaultValue: 'Show less' })
                            : i18n.t('mainLanding.feed.showMoreComments', { defaultValue: 'See {{count}} more comments...', count: comments.length - 3 })}
                        </button>
                      )}
                    </div>

                    {/* ADD COMMENT: remove possibility if user null */}
                    {isAuthed ? (
                      <div className="add-comment">
                        <div className="add-comment-wrapper">
                          <div className="comment-input-container">
                            <input
                              type="text"
                              placeholder={i18n.t('mainLanding.feed.addCommentPlaceholder', { defaultValue: 'Add a comment...' })}
                              value={commentInputs[post.id] || ''}
                              onChange={(e) => handleCommentChange(post.id, e.target.value)}
                              onKeyPress={(e) => handleKeyPress(e, post.id)}
                              className="comment-input"
                            />
                            <button
                              onClick={() => handleCommentSubmit(post.id)}
                              className="comment-submit-btn"
                              disabled={!commentInputs[post.id]?.trim()}
                              type="button"
                            >
                              {i18n.t('mainLanding.feed.postComment', { defaultValue: 'Post' })}
                            </button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="loginHint">{i18n.t('mainLanding.feed.loginToComment', { defaultValue: 'Log in to comment' })}</div>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default FeedPage;
